import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  emptyProfile,
  MAX_PROFILES_PER_CASE,
  type CaseFileMeta,
  type CaseProfile,
  type FileKind,
} from './types';

export interface CaseSubmissionReceipt {
  createdCaseIds: number[];
  createdCount: number;
  submittedAt: string;
  signatureDataUrl: string | null;
}

interface CaseBuilderState {
  pricingConfirmed: boolean;
  otpVerified: boolean;
  profiles: CaseProfile[];
  signatureDataUrl: string | null;
  declarationAccepted: boolean;
  receipt: CaseSubmissionReceipt | null;

  setPricing: (v: boolean) => void;
  setOtp: (v: boolean) => void;
  addProfile: () => void;
  removeProfile: (id: string) => void;
  updateProfile: (id: string, updater: (p: CaseProfile) => CaseProfile) => void;
  setProfileFile: (id: string, kind: FileKind, meta: CaseFileMeta | null) => void;
  setSignature: (dataUrl: string | null) => void;
  setDeclaration: (v: boolean) => void;
  setReceipt: (r: CaseSubmissionReceipt | null) => void;
  startFresh: () => void;
  reset: () => void;
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Fallback for environments without crypto.randomUUID.
    return `p-${Date.now()}-${(globalThis.performance?.now?.() ?? 0).toFixed(0)}`;
  }
}

const FILE_FIELD: Record<FileKind, 'resume_file' | 'employee_consent_file'> = {
  employee_resume: 'resume_file',
  employee_consent: 'employee_consent_file',
};

const base = {
  pricingConfirmed: false,
  otpVerified: false,
  signatureDataUrl: null as string | null,
  declarationAccepted: false,
  receipt: null as CaseSubmissionReceipt | null,
};

export const useCaseBuilderStore = create<CaseBuilderState>()(
  persist(
    (set) => ({
      ...base,
      profiles: [emptyProfile(newId())],

      setPricing: (v) => set({ pricingConfirmed: v }),
      setOtp: (v) => set({ otpVerified: v }),
      addProfile: () =>
        set((s) =>
          s.profiles.length >= MAX_PROFILES_PER_CASE
            ? s
            : { profiles: [...s.profiles, emptyProfile(newId())] },
        ),
      removeProfile: (id) =>
        set((s) => ({ profiles: s.profiles.filter((p) => p.profile_id !== id) })),
      updateProfile: (id, updater) =>
        set((s) => ({
          profiles: s.profiles.map((p) => (p.profile_id === id ? updater(p) : p)),
        })),
      setProfileFile: (id, kind, meta) =>
        set((s) => ({
          profiles: s.profiles.map((p) =>
            p.profile_id === id ? { ...p, [FILE_FIELD[kind]]: meta } : p,
          ),
        })),
      setSignature: (dataUrl) => set({ signatureDataUrl: dataUrl }),
      setDeclaration: (v) => set({ declarationAccepted: v }),
      setReceipt: (r) => set({ receipt: r }),
      startFresh: () => set({ ...base, profiles: [emptyProfile(newId())] }),
      reset: () => set({ ...base, profiles: [emptyProfile(newId())] }),
    }),
    {
      name: 'tally:employer-case-builder',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
